import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buttonStyleGreen, inputStyle } from '../styles';
import { DEFAULT_CROP, FEEDBACK_QUESTION, FEEDBACK_QUESTION_OPTIONS, saveCampaign } from '../services/campaignStore';
import {
  DISCOVERY_INTERESTS,
  getDestinationPreview,
  getDestinationCtaLabel,
  getDestinationEmbed,
  getCompletionTokenRate,
  getFeedbackQuestion,
  getInterestLabels,
  getLearnMorePreview,
  getMediaPreview,
  getDestinationProvider,
  getMediaProviderLabels,
  getSuggestedDestinationProviders,
  normalizeInterestIds,
  toCampaignDestination
} from '../services/discoveryConfig';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const DISCOVERY_PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'Twitter', 'LinkedIn', 'Facebook'];
const CREATOR_TYPES = [
  'Music',
  'Film and Video',
  'Books and Writing',
  'Business',
  'Education',
  'Health and Wellness',
  'Art and Design',
  'Community'
];

function PostLink({ userTokens, userProfile, onNavigateToHome, onNavigateToBuyTokens, editingCampaign }) {
  const fileInputRef = useRef(null);
  const targetInterestsRef = useRef(null);
  const platformRef = useRef(null);
  const creatorTypeRef = useRef(null);
  const creatorNameRef = useRef(null);
  const campaignTitleRef = useRef(null);
  const internalMessageRef = useRef(null);
  const destinationUrlRef = useRef(null);
  const fieldRefs = {
    targetInterestIds: targetInterestsRef,
    platform: platformRef,
    creatorType: creatorTypeRef,
    creatorName: creatorNameRef,
    campaignTitle: campaignTitleRef,
    internalMessage: internalMessageRef,
    destinationUrl: destinationUrlRef,
    visualFile: fileInputRef
  };
  const [targetInterestIds, setTargetInterestIds] = useState([]);
  const [platform, setPlatform] = useState('');
  const [creatorType, setCreatorType] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [campaignTitle, setCampaignTitle] = useState('');
  const [internalMessage, setInternalMessage] = useState('');
  const [visualFile, setVisualFile] = useState(null);
  const [visualPreviewUrl, setVisualPreviewUrl] = useState('');
  const [visualError, setVisualError] = useState('');
  const [appliedCrop, setAppliedCrop] = useState(DEFAULT_CROP);
  const [destinationPlatformId, setDestinationPlatformId] = useState('spotify');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [mediaLink, setMediaLink] = useState('');
  const [learnMoreLink, setLearnMoreLink] = useState('');
  const [feedbackQuestion, setFeedbackQuestion] = useState(FEEDBACK_QUESTION);
  const [showPreview, setShowPreview] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [notice, setNotice] = useState(null);
  const [savedDraftId, setSavedDraftId] = useState(null);
  const [lastStatus, setLastStatus] = useState('Draft');

  useEffect(() => {
    if (!editingCampaign) return;

    setTargetInterestIds(normalizeInterestIds(editingCampaign.targetInterestIds));
    setPlatform(editingCampaign.platform || '');
    setCreatorType(editingCampaign.creatorType || '');
    setCreatorName(editingCampaign.creatorName || '');
    setCampaignTitle(editingCampaign.title || '');
    setInternalMessage(editingCampaign.message || '');
    setVisualFile(editingCampaign.visual?.blob || null);
    setVisualError('');
    setAppliedCrop(editingCampaign.visual?.crop || DEFAULT_CROP);
    setDestinationPlatformId(editingCampaign.destination?.platformId || 'website');
    setDestinationUrl(editingCampaign.destination?.url || '');
    setMediaLink(editingCampaign.mediaLink || editingCampaign.mediaPreview?.href || '');
    setLearnMoreLink(editingCampaign.learnMoreLink || editingCampaign.learnMorePreview?.href || '');
    setFeedbackQuestion(getFeedbackQuestion(editingCampaign.feedbackQuestion));
    setSavedDraftId(editingCampaign.id || null);
    setLastStatus(editingCampaign.status || 'Draft');
    setMissingFields([]);
    setNotice({
      type: 'success',
      message: 'Editing an existing local campaign. Saving will update this campaign.'
    });
    setShowPreview(Boolean(editingCampaign.visual?.blob));
  }, [editingCampaign]);

  useEffect(() => {
    if (!visualFile) {
      setVisualPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(visualFile);
    setVisualPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [visualFile]);

  const mediaPreview = useMemo(() => getMediaPreview(mediaLink), [mediaLink]);
  const learnMorePreview = useMemo(() => getLearnMorePreview(learnMoreLink), [learnMoreLink]);
  const destinationPreview = useMemo(
    () => getDestinationPreview(destinationPlatformId, destinationUrl),
    [destinationPlatformId, destinationUrl]
  );
  const destinationProviderOptions = useMemo(
    () => {
      const options = getSuggestedDestinationProviders({ creatorType, discoveryPlatform: platform });
      if (destinationPlatformId === 'website' && !options.some((provider) => provider.id === 'website')) {
        const websiteProvider = getDestinationProvider('website');
        return websiteProvider ? [...options, websiteProvider] : options;
      }
      return options;
    },
    [creatorType, destinationPlatformId, platform]
  );
  const mediaProviderLabels = useMemo(() => getMediaProviderLabels().join(', '), []);
  const completionTokenRate = useMemo(() => getCompletionTokenRate(targetInterestIds), [targetInterestIds]);
  const selectedInterestLabels = useMemo(() => getInterestLabels(targetInterestIds), [targetInterestIds]);
  const preservedLegacyLearnMoreLink = editingCampaign?.learnMoreLink || editingCampaign?.learnMorePreview?.href || '';
  const preservedLegacyLearnMorePreview = editingCampaign?.learnMorePreview || learnMorePreview;
  const showLegacyLearnMore = false;
  const missingFieldDetails = useMemo(() => getMissingFieldDetails({
    targetInterestIds,
    creatorType,
    platform,
    creatorName,
    campaignTitle,
    internalMessage,
    visualFile,
    visualError,
    destinationUrl,
    destinationPreview
  }), [targetInterestIds, platform, creatorType, creatorName, campaignTitle, internalMessage, visualFile, visualError, destinationUrl, destinationPreview]);

  const handleVisualChange = (event) => {
    const file = event.target.files?.[0];
    setShowPreview(false);
    setNotice(null);

    if (!file) {
      clearImage();
      return;
    }

    if (!IMAGE_TYPES.includes(file.type)) {
      clearImage();
      setVisualError('Please choose a JPG, PNG, or WebP image.');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      clearImage();
      setVisualError('Please choose an image that is 5 MB or smaller.');
      return;
    }

    setVisualFile(file);
    setVisualError('');
    resetCrop();
    setMissingFields((fields) => fields.filter((field) => field !== 'visualFile'));
  };

  const handleTargetInterestToggle = (interestId) => {
    setTargetInterestIds((currentIds) => {
      const current = normalizeInterestIds(currentIds);
      const selected = current.includes(interestId);
      const nextIds = selected
        ? current.filter((id) => id !== interestId)
        : current.length < 3
          ? [...current, interestId]
          : current;
      return nextIds;
    });
    setMissingFields((fields) => fields.filter((field) => field !== 'targetInterestIds'));
    setShowPreview(false);
    setNotice(null);
  };

  const clearImage = () => {
    setVisualFile(null);
    setVisualError('');
    resetCrop();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetCrop = () => {
    setAppliedCrop(DEFAULT_CROP);
  };

  const changeImage = () => {
    fileInputRef.current?.click();
  };

  const handlePreview = () => {
    const missing = getMissingFieldDetails({
      targetInterestIds,
      creatorType,
      platform,
      creatorName,
      campaignTitle,
      internalMessage,
      visualFile,
      visualError,
      destinationUrl,
      destinationPreview
    });

    if (missing.length > 0) {
      setMissingFields(missing.map((field) => field.id));
      fieldRefs[missing[0].id]?.current?.focus();
      return;
    }

    setMissingFields([]);
    setShowPreview(true);
  };

  const handleRequiredFieldChange = (id, setter, value) => {
    updateField(setter, value, setShowPreview);
    setMissingFields((fields) => fields.filter((field) => field !== id));
    setNotice(null);
  };

  const getCampaignRecord = (status) => ({
    id: savedDraftId,
    createdAt: editingCampaign?.createdAt,
    publishedAt: status === 'Active' ? editingCampaign?.publishedAt : editingCampaign?.publishedAt || null,
    creatorId: userProfile?.uid || userProfile?.email || 'local-creator',
    creatorName: creatorName.trim(),
    creatorType,
    platform,
    targetInterestIds: normalizeInterestIds(targetInterestIds),
    completionTokenRate,
    audienceMode: 'targeted',
    title: campaignTitle.trim() || 'Untitled Campaign',
    message: internalMessage.trim(),
    visual: visualFile ? {
      blob: visualFile,
      name: visualFile.name || editingCampaign?.visual?.name || 'campaign-artwork',
      type: visualFile.type || editingCampaign?.visual?.type || '',
      size: visualFile.size || editingCampaign?.visual?.size || 0,
      crop: appliedCrop
    } : null,
    mediaLink: mediaPreview.status === 'valid' ? mediaLink.trim() : '',
    mediaPreview,
    destination: toCampaignDestination(destinationPreview),
    learnMoreLink: editingCampaign ? preservedLegacyLearnMoreLink : '',
    learnMorePreview: editingCampaign ? preservedLegacyLearnMorePreview : { status: 'empty' },
    feedbackQuestion: getFeedbackQuestion(feedbackQuestion),
    status
  });

  const handleSaveDraft = async () => {
    try {
      const saved = await saveCampaign(getCampaignRecord('Draft'));
      setSavedDraftId(saved.id);
      setLastStatus(saved.status);
      setNotice({ type: 'success', message: 'Draft saved privately in this browser.' });
    } catch (error) {
      setNotice({ type: 'error', message: `Draft could not be saved: ${error.message}` });
    }
  };

  const handlePublish = async () => {
    const missing = getMissingFieldDetails({
      targetInterestIds,
      creatorType,
      platform,
      creatorName,
      campaignTitle,
      internalMessage,
      visualFile,
      visualError,
      destinationUrl,
      destinationPreview
    });

    if (missing.length > 0) {
      setMissingFields(missing.map((field) => field.id));
      fieldRefs[missing[0].id]?.current?.focus();
      setNotice({ type: 'error', message: `Publish needs: ${missing.map((field) => field.label).join(', ')}.` });
      return;
    }

    try {
      const saved = await saveCampaign(getCampaignRecord('Active'));
      setSavedDraftId(saved.id);
      setLastStatus(saved.status);
      setShowPreview(true);
      setMissingFields([]);
      setNotice({
        type: 'success',
        message: `Published to Discovery for ${selectedInterestLabels.join(', ')} in this browser. It will survive refresh on this device.`
      });
    } catch (error) {
      setNotice({ type: 'error', message: `Campaign could not be published: ${error.message}` });
    }
  };

  return (
    <div style={{ padding: '30px 16px', maxWidth: 1120, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 30 }}>
        <h1 style={{ fontSize: 32, color: '#333', marginBottom: 10 }}>Create Discovery Campaign</h1>
        <p style={{ fontSize: 16, color: '#666', marginBottom: 20 }}>
          Build a local preview of the internal discovery card creators will eventually publish.
        </p>
        <p style={localOnlyNoteStyle}>
          Local-only Stage 4: published cards and feedback stay in this browser. Cross-device and cross-user publishing will not work until the Firebase stage.
        </p>

        {userTokens < 10 && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: 8,
            padding: 15,
            marginBottom: 20
          }}>
            <strong>Low Token Balance:</strong> You have {userTokens} tokens.
            <span> This preview does not spend or award tokens.</span>
            <button
              onClick={onNavigateToBuyTokens}
              style={{
                marginLeft: 10,
                backgroundColor: '#ffc107',
                border: 'none',
                borderRadius: 5,
                padding: '5px 10px',
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Buy Tokens
            </button>
          </div>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 24,
        alignItems: 'start'
      }}>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            handlePreview();
          }}
          style={panelStyle}
        >
          <fieldset
            ref={targetInterestsRef}
            style={getAudienceFieldsetStyle(missingFields)}
            aria-invalid={missingFields.includes('targetInterestIds')}
          >
            <legend style={labelStyle}>Target Interests</legend>
            <small style={helperStyle}>Choose 1 to 3 discovery interests. This is the audience, not your profession.</small>
            <div style={interestGridStyle}>
              {DISCOVERY_INTERESTS.map((interest) => {
                const checked = targetInterestIds.includes(interest.id);
                const disabled = !checked && targetInterestIds.length >= 3;
                return (
                  <label key={interest.id} style={interestOptionStyle(checked, disabled)}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => handleTargetInterestToggle(interest.id)}
                    />
                    <span>{interest.label}</span>
                  </label>
                );
              })}
            </div>
            <div style={rateSummaryStyle}>
              <strong>Completion reward/cost:</strong>{' '}
              {completionTokenRate > 0 ? `${completionTokenRate} tokens` : 'Select interests to calculate'}
            </div>
          </fieldset>

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="discovery-platform" style={labelStyle}>Legacy Discovery Category</label>
            <select
              ref={platformRef}
              id="discovery-platform"
              value={platform}
              onChange={(e) => handleRequiredFieldChange('platform', setPlatform, e.target.value)}
              style={getFieldStyle('platform', missingFields)}
              aria-invalid={missingFields.includes('platform')}
            >
              <option value="">No legacy category</option>
              {DISCOVERY_PLATFORMS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
            <small style={helperStyle}>Preserved for older campaigns; the viewer feed now uses interests.</small>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="creator-type" style={labelStyle}>Creator Type</label>
            <select
              ref={creatorTypeRef}
              id="creator-type"
              value={creatorType}
              onChange={(e) => handleRequiredFieldChange('creatorType', setCreatorType, e.target.value)}
              style={getFieldStyle('creatorType', missingFields)}
              aria-invalid={missingFields.includes('creatorType')}
              required
            >
              <option value="">Select Creator Type</option>
              {CREATOR_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="creator-name" style={labelStyle}>Creator Name</label>
            <input
              ref={creatorNameRef}
              id="creator-name"
              type="text"
              value={creatorName}
              onChange={(e) => handleRequiredFieldChange('creatorName', setCreatorName, e.target.value)}
              style={getFieldStyle('creatorName', missingFields)}
              aria-invalid={missingFields.includes('creatorName')}
              placeholder="Creator or brand name"
              required
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="campaign-title" style={labelStyle}>Campaign Title</label>
            <input
              ref={campaignTitleRef}
              id="campaign-title"
              type="text"
              value={campaignTitle}
              onChange={(e) => handleRequiredFieldChange('campaignTitle', setCampaignTitle, e.target.value)}
              style={getFieldStyle('campaignTitle', missingFields)}
              aria-invalid={missingFields.includes('campaignTitle')}
              placeholder="What should people discover?"
              required
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="internal-message" style={labelStyle}>Campaign Message</label>
            <textarea
              id="internal-message"
              ref={internalMessageRef}
              value={internalMessage}
              onChange={(e) => handleRequiredFieldChange('internalMessage', setInternalMessage, e.target.value.slice(0, 500))}
              maxLength={500}
              rows={5}
              style={getTextareaStyle('internalMessage', missingFields)}
              aria-invalid={missingFields.includes('internalMessage')}
              placeholder="Tell PromoGrind users what to notice, understand, or react to."
              required
            />
            <small style={helperStyle}>{internalMessage.length}/500 characters</small>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="campaign-visual" style={labelStyle}>Campaign Visual</label>
            <input
              ref={fileInputRef}
              id="campaign-visual"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleVisualChange}
              style={{ ...getFieldStyle('visualFile', missingFields), padding: 8 }}
              aria-invalid={missingFields.includes('visualFile') || Boolean(visualError)}
              required
            />
            <small style={helperStyle}>Required for preview. JPG, PNG, or WebP only. Maximum 5 MB. Local preview only.</small>
            {visualFile && !visualError && <div style={successStyle}>Photo ready</div>}
            {visualError && <div style={errorStyle}>{visualError}</div>}
          </div>

          {visualPreviewUrl && (
            <ImageCropTool
              imageUrl={visualPreviewUrl}
              crop={appliedCrop}
              onCropChange={(nextCrop) => {
                setAppliedCrop(nextCrop);
              }}
              onReset={resetCrop}
              onChangeImage={changeImage}
            />
          )}

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="destination-platform" style={labelStyle}>Primary Destination</label>
            <select
              id="destination-platform"
              value={destinationPlatformId}
              onChange={(e) => {
                setDestinationPlatformId(e.target.value);
                setShowPreview(false);
                setNotice(null);
              }}
              style={inputStyle}
            >
              {destinationProviderOptions.map((provider) => (
                <option key={provider.id} value={provider.id}>{provider.label}</option>
              ))}
            </select>
            <small style={helperStyle}>Choose where people should go for the thing you are promoting.</small>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="destination-url" style={labelStyle}>Destination Link</label>
            <input
              ref={destinationUrlRef}
              id="destination-url"
              type="url"
              value={destinationUrl}
              onChange={(e) => handleRequiredFieldChange('destinationUrl', setDestinationUrl, e.target.value)}
              style={getFieldStyle('destinationUrl', missingFields)}
              aria-invalid={missingFields.includes('destinationUrl')}
              placeholder="https://your-primary-destination.com"
              required
            />
            <small style={helperStyle}>Choose an approved provider. Arbitrary websites are not enabled for new V1 campaigns.</small>
            {destinationUrl && <div style={destinationPreview.status === 'invalid' ? errorStyle : helperStyle}>{destinationPreview.message}</div>}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="media-link" style={labelStyle}>Optional Music or Video Link</label>
            <input id="media-link" type="url" value={mediaLink} onChange={(e) => updateField(setMediaLink, e.target.value, setShowPreview)} style={inputStyle} placeholder="https://open.spotify.com/..." />
            <small style={helperStyle}>{mediaProviderLabels} are recognized. Paste normal HTTPS links only.</small>
            {mediaLink && <div style={mediaPreview.status === 'invalid' ? errorStyle : helperStyle}>{mediaPreview.message}</div>}
          </div>

          {showLegacyLearnMore && (
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="learn-more-link" style={labelStyle}>Optional Learn More Link</label>
            <input id="learn-more-link" type="url" value={learnMoreLink} onChange={(e) => updateField(setLearnMoreLink, e.target.value, setShowPreview)} style={inputStyle} placeholder="https://trusted-provider-or-official-site.com" />
            <small style={helperStyle}>Legacy field preserved for this campaign. New campaigns use one primary destination.</small>
            {learnMoreLink && <div style={learnMorePreview.status === 'invalid' ? errorStyle : helperStyle}>{learnMorePreview.message}</div>}
          </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="feedback-question" style={labelStyle}>Feedback Question</label>
            <select
              id="feedback-question"
              value={feedbackQuestion}
              onChange={(e) => updateField(setFeedbackQuestion, e.target.value, setShowPreview)}
              style={inputStyle}
            >
              {FEEDBACK_QUESTION_OPTIONS.map((question) => (
                <option key={question} value={question}>{question}</option>
              ))}
            </select>
            <small style={helperStyle}>Responses stay Yes, Maybe, or No.</small>
          </div>

          {missingFields.length > 0 && (
            <div role="alert" style={missingSummaryStyle}>
              <strong>Preview needs:</strong> {missingFieldDetails.map((field) => field.label).join(', ')}.
            </div>
          )}

          {notice && (
            <div role="status" aria-live="polite" style={notice.type === 'error' ? noticeErrorStyle : noticeSuccessStyle}>
              {notice.message}
            </div>
          )}

          <div style={actionGridStyle}>
            <button type="button" onClick={handleSaveDraft} style={draftButtonStyle}>
              Save Draft
            </button>
            <button type="submit" style={{ ...buttonStyleGreen, width: '100%', fontSize: 16, padding: '13px', marginTop: 0 }}>
              Preview Card
            </button>
            <button type="button" onClick={handlePublish} style={publishButtonStyle}>
              Publish to Discovery
            </button>
          </div>

          <button type="button" onClick={onNavigateToHome} style={secondaryButtonStyle}>
            Return to Dashboard
          </button>
        </form>

        <div style={{ ...panelStyle, padding: 0, overflow: 'hidden' }}>
          {showPreview ? (
            <DiscoveryCardPreview
              creatorType={creatorType}
              creatorName={creatorName}
              campaignTitle={campaignTitle}
              internalMessage={internalMessage}
              targetInterestIds={targetInterestIds}
              completionTokenRate={completionTokenRate}
              platform={platform}
              status={lastStatus}
              visualPreviewUrl={visualPreviewUrl}
              crop={appliedCrop}
              destinationPreview={destinationPreview}
              mediaPreview={mediaPreview}
              learnMorePreview={learnMorePreview}
              feedbackQuestion={getFeedbackQuestion(feedbackQuestion)}
            />
          ) : (
            <div style={{ padding: 30, color: '#666', textAlign: 'center' }}>
              Complete the required fields, then preview the Discovery Card.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImageCropTool({ imageUrl, crop, onCropChange, onReset, onChangeImage }) {
  const frameRef = useRef(null);
  const dragStateRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);

  const handlePointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startCrop: crop
    };
  };

  const handlePointerMove = (event) => {
    const dragState = dragStateRef.current;
    const frame = frameRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId || !frame) return;

    const rect = frame.getBoundingClientRect();
    const dx = ((event.clientX - dragState.startX) / rect.width) * 100;
    const dy = ((event.clientY - dragState.startY) / rect.height) * 100;

    onCropChange({
      ...crop,
      x: clamp(dragState.startCrop.x + dx, 0, 100),
      y: clamp(dragState.startCrop.y + dy, 0, 100)
    });
    setHasDragged(true);
  };

  const handlePointerEnd = (event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragStateRef.current = null;
    setIsDragging(false);
  };

  const handleKeyDown = (event) => {
    const arrowDeltas = {
      ArrowLeft: { x: -1, y: 0 },
      ArrowRight: { x: 1, y: 0 },
      ArrowUp: { x: 0, y: -1 },
      ArrowDown: { x: 0, y: 1 }
    };
    const delta = arrowDeltas[event.key];
    if (!delta) return;

    event.preventDefault();
    const step = event.shiftKey ? 5 : 1;
    onCropChange({
      ...crop,
      x: clamp(crop.x + delta.x * step, 0, 100),
      y: clamp(crop.y + delta.y * step, 0, 100)
    });
    setHasDragged(true);
  };

  const handleReset = () => {
    setHasDragged(false);
    onReset();
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <label style={labelStyle}>Crop and Position</label>
      <p id="crop-instructions" style={helperStyle}>Drag the photo to reposition it. Use the slider below to zoom.</p>
      <span id="crop-screen-reader-instructions" style={visuallyHiddenStyle}>
        Use arrow keys to reposition the photo. Hold Shift with an arrow key to move faster.
      </span>
      <div
        ref={frameRef}
        role="img"
        tabIndex={0}
        aria-label="16 by 9 crop preview"
        aria-describedby="crop-instructions crop-screen-reader-instructions"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onKeyDown={handleKeyDown}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          borderRadius: 10,
          overflow: 'hidden',
          border: '2px solid #007bff',
          backgroundColor: '#111',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
          marginBottom: 12
        }}
      >
        <CroppedImage imageUrl={imageUrl} crop={crop} />
        <div aria-hidden="true" style={{ ...moveIconStyle, opacity: hasDragged ? 0 : 1 }}>
          <span style={moveIconGlyphStyle}>
            <span style={moveIconHorizontalStyle}>↔</span>
            <span style={moveIconVerticalStyle}>↕</span>
          </span>
        </div>
      </div>

      <label htmlFor="crop-zoom" style={{ ...labelStyle, fontSize: 14 }}>Zoom ({Math.round(crop.zoom * 100)}%)</label>
      <input
        id="crop-zoom"
        type="range"
        min="1"
        max="2.5"
        step="0.01"
        value={crop.zoom}
        onChange={(event) => onCropChange({ ...crop, zoom: Number(event.target.value) })}
        style={{ width: '100%', marginBottom: 12 }}
      />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={handleReset} style={smallButtonStyle}>Reset Crop</button>
        <button type="button" onClick={onChangeImage} style={smallButtonStyle}>Change Image</button>
      </div>
    </div>
  );
}

function CroppedImage({ imageUrl, crop }) {
  return (
    <img
      src={imageUrl}
      alt=""
      draggable="false"
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: `${crop.x}% ${crop.y}%`,
        transform: `scale(${crop.zoom})`,
        transformOrigin: `${crop.x}% ${crop.y}%`,
        pointerEvents: 'none'
      }}
    />
  );
}

function DiscoveryCardPreview({ creatorType, creatorName, campaignTitle, internalMessage, targetInterestIds, completionTokenRate, platform, status, visualPreviewUrl, crop, destinationPreview, mediaPreview, learnMorePreview, feedbackQuestion }) {
  const destination = toCampaignDestination(destinationPreview);
  const interestLabels = getInterestLabels(targetInterestIds);

  return (
    <article aria-label="Discovery card preview">
      <div style={{ width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', backgroundColor: '#e9ecef' }}>
        <CroppedImage imageUrl={visualPreviewUrl} crop={crop} />
      </div>
      <div style={{ padding: 24 }}>
        <div style={pillStyle}>{creatorType}</div>
        <div style={statusRowStyle}>
          <span style={statusBadgeStyle}>{status || 'Draft'}</span>
          {interestLabels.map((label) => <span key={label} style={platformBadgeStyle}>{label}</span>)}
          {platform && <span style={legacyBadgeStyle}>{platform}</span>}
        </div>
        <div style={previewRateStyle}>Future completed Discovery rate: {completionTokenRate || 0} tokens</div>
        <h2 style={{ margin: '0 0 6px 0', color: '#333' }}>{campaignTitle}</h2>
        <p style={{ margin: '0 0 16px 0', color: '#555', fontWeight: 'bold' }}>{creatorName}</p>
        <p style={{ color: '#444', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{internalMessage}</p>

        {destination && (
          <DestinationPreview
            destination={destination}
            visualUrl={visualPreviewUrl}
            title={campaignTitle}
          />
        )}

        {mediaPreview.status !== 'empty' && (
          <div style={{ marginTop: 20 }}>
            <h3 style={{ color: '#333', fontSize: 16, marginBottom: 10 }}>Optional Media</h3>
            <TrustedMediaPreview mediaPreview={mediaPreview} />
          </div>
        )}

        <div style={feedbackPreviewStyle}>
          <strong>{feedbackQuestion || FEEDBACK_QUESTION}</strong>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
            {['Yes', 'Maybe', 'No'].map((option) => <span key={option} style={choicePreviewStyle}>{option}</span>)}
          </div>
        </div>

        {learnMorePreview.status !== 'empty' && (
          <div style={{ marginTop: 20 }}>
            {learnMorePreview.status === 'invalid' ? (
              <small style={{ display: 'block', color: '#666' }}>{learnMorePreview.message}</small>
            ) : learnMorePreview.clickable ? (
              <a href={learnMorePreview.href} target="_blank" rel="noopener noreferrer" style={learnMoreButtonStyle}>Learn More</a>
            ) : (
              <div>
                <button type="button" disabled style={{ ...learnMoreButtonStyle, opacity: 0.6, cursor: 'not-allowed' }}>Learn More</button>
                <small style={{ display: 'block', color: '#666', marginTop: 8 }}>
                  Other official website - requires review before this link can be opened from PromoGrind.
                </small>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function DestinationPreview({ destination, visualUrl, title }) {
  const embedUrl = getDestinationEmbed(destination);
  const ctaLabel = getDestinationCtaLabel(destination);
  const hasArtwork = Boolean(visualUrl);

  return (
    <section style={hasArtwork && !embedUrl ? compactDestinationPreviewStyle : destinationPreviewStyle} aria-label={`${destination.label} destination preview`}>
      <div style={destinationHeaderStyle}>
        <span style={destinationProviderPillStyle}>{destination.label}</span>
        <strong>{ctaLabel}</strong>
      </div>

      {embedUrl ? (
        <iframe
          title={`${destination.label} destination preview`}
          src={embedUrl}
          loading="lazy"
          allow="encrypted-media; fullscreen; picture-in-picture"
          sandbox="allow-scripts allow-same-origin allow-popups"
          style={destinationEmbedStyle}
        />
      ) : !hasArtwork ? (
        <div style={destinationFallbackVisualStyle}>
          <div style={destinationBrandFallbackStyle}>
            <strong>PromoGrind</strong>
            <span>Discovery destination</span>
          </div>
          <div style={destinationOverlayStyle}>
            <span>{destination.label}</span>
            <strong>{title || 'Campaign destination'}</strong>
          </div>
        </div>
      ) : (
        <p style={compactDestinationCopyStyle}>
          Continue from the campaign artwork to this external destination.
        </p>
      )}

      <a href={destination.url} target="_blank" rel="noopener noreferrer" style={destinationButtonStyle}>
        {ctaLabel}
      </a>
      <small style={{ display: 'block', color: '#666', marginTop: 8, textAlign: 'center' }}>
        Primary external destination. External visits do not create rewards.
      </small>
    </section>
  );
}

function TrustedMediaPreview({ mediaPreview }) {
  if (mediaPreview.embedUrl) {
    return (
      <iframe
        title={`${mediaPreview.provider} media preview`}
        src={mediaPreview.embedUrl}
        loading="lazy"
        allow="encrypted-media; fullscreen; picture-in-picture"
        sandbox="allow-scripts allow-same-origin allow-popups"
        style={{
          width: '100%',
          minHeight: mediaPreview.provider === 'Spotify' || mediaPreview.provider === 'Apple Music' ? 160 : 260,
          border: '1px solid #ddd',
          borderRadius: 10
        }}
      />
    );
  }

  if (mediaPreview.href) {
    return (
      <a href={mediaPreview.href} target="_blank" rel="noopener noreferrer" style={trustedLinkStyle}>
        Open trusted {mediaPreview.provider} link
      </a>
    );
  }

  return <p style={{ color: '#666' }}>{mediaPreview.message}</p>;
}

function updateField(setter, value, setShowPreview) {
  setter(value);
  setShowPreview(false);
}

function getMissingFieldDetails(fields) {
  const missing = [];
  if (normalizeInterestIds(fields.targetInterestIds).length < 1) missing.push({ id: 'targetInterestIds', label: '1 to 3 Target Interests' });
  if (!fields.creatorType) missing.push({ id: 'creatorType', label: 'Creator Type' });
  if (!fields.creatorName.trim()) missing.push({ id: 'creatorName', label: 'Creator Name' });
  if (!fields.campaignTitle.trim()) missing.push({ id: 'campaignTitle', label: 'Campaign Title' });
  if (!fields.internalMessage.trim()) missing.push({ id: 'internalMessage', label: 'Campaign Message' });
  if (!fields.visualFile || fields.visualError) missing.push({ id: 'visualFile', label: 'a valid Campaign Visual' });
  if (!fields.destinationUrl.trim() || fields.destinationPreview?.status !== 'valid') {
    missing.push({ id: 'destinationUrl', label: 'a valid Destination Link' });
  }
  return missing;
}

function getFieldStyle(id, missingFields) {
  if (!missingFields.includes(id)) return inputStyle;
  return {
    ...inputStyle,
    borderColor: '#dc3545',
    boxShadow: '0 0 0 3px rgba(220, 53, 69, 0.15)'
  };
}

function getTextareaStyle(id, missingFields) {
  if (!missingFields.includes(id)) return textareaStyle;
  return {
    ...textareaStyle,
    borderColor: '#dc3545',
    boxShadow: '0 0 0 3px rgba(220, 53, 69, 0.15)'
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

const panelStyle = {
  backgroundColor: 'white',
  padding: 30,
  borderRadius: 15,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
};

const getAudienceFieldsetStyle = (missingFields) => ({
  border: `1px solid ${missingFields.includes('targetInterestIds') ? '#dc3545' : '#ddd'}`,
  borderRadius: 10,
  margin: '0 0 20px 0',
  padding: 16,
  boxShadow: missingFields.includes('targetInterestIds') ? '0 0 0 3px rgba(220, 53, 69, 0.15)' : 'none'
});

const interestGridStyle = {
  display: 'grid',
  gap: 8,
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  marginTop: 12
};

const interestOptionStyle = (checked, disabled) => ({
  alignItems: 'center',
  backgroundColor: checked ? '#eef7ff' : '#ffffff',
  border: `1px solid ${checked ? '#007bff' : '#d8dee6'}`,
  borderRadius: 8,
  color: disabled ? '#9aa4b2' : '#333',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  fontSize: 14,
  fontWeight: checked ? 'bold' : 'normal',
  gap: 8,
  minHeight: 42,
  padding: '9px 10px'
});

const rateSummaryStyle = {
  backgroundColor: '#f8f9fa',
  border: '1px solid #d8dee6',
  borderRadius: 8,
  color: '#25364a',
  marginTop: 12,
  padding: 12
};

const labelStyle = {
  display: 'block',
  fontWeight: 'bold',
  marginBottom: 8,
  color: '#333'
};

const helperStyle = {
  color: '#666',
  fontSize: 12,
  display: 'block',
  marginTop: 5
};

const localOnlyNoteStyle = {
  backgroundColor: '#fff8e1',
  border: '1px solid #ffe08a',
  borderRadius: 8,
  color: '#6b5300',
  display: 'inline-block',
  fontSize: 13,
  lineHeight: 1.4,
  margin: '0 auto 20px auto',
  maxWidth: 720,
  padding: '10px 14px'
};

const errorStyle = {
  color: '#dc3545',
  fontSize: 12,
  marginTop: 5
};

const noticeSuccessStyle = {
  backgroundColor: '#d4edda',
  border: '1px solid #28a745',
  borderRadius: 8,
  color: '#155724',
  fontSize: 14,
  fontWeight: 'bold',
  marginBottom: 15,
  padding: 12
};

const noticeErrorStyle = {
  backgroundColor: '#fff3f3',
  border: '1px solid #f1aeb5',
  borderRadius: 8,
  color: '#842029',
  fontSize: 14,
  fontWeight: 'bold',
  marginBottom: 15,
  padding: 12
};

const actionGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 10
};

const draftButtonStyle = {
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 16,
  fontWeight: 'bold',
  padding: 13,
  width: '100%'
};

const publishButtonStyle = {
  backgroundColor: '#198754',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 16,
  fontWeight: 'bold',
  padding: 13,
  width: '100%'
};

const statusRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 12
};

const statusBadgeStyle = {
  backgroundColor: '#effaf1',
  border: '1px solid #28a745',
  borderRadius: 999,
  color: '#155724',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 'bold',
  padding: '5px 10px'
};

const platformBadgeStyle = {
  backgroundColor: '#f8f9fa',
  border: '1px solid #ced4da',
  borderRadius: 999,
  color: '#495057',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 'bold',
  padding: '5px 10px'
};

const legacyBadgeStyle = {
  ...platformBadgeStyle,
  backgroundColor: '#fff8e1',
  border: '1px solid #ffe08a',
  color: '#6b5300'
};

const previewRateStyle = {
  backgroundColor: '#f8fbff',
  border: '1px solid #d8dee6',
  borderRadius: 8,
  color: '#25364a',
  fontSize: 14,
  fontWeight: 'bold',
  marginBottom: 14,
  padding: 10
};

const successStyle = {
  color: '#198754',
  fontSize: 12,
  fontWeight: 'bold',
  marginTop: 5
};

const missingSummaryStyle = {
  backgroundColor: '#fff3f3',
  border: '1px solid #f1aeb5',
  borderRadius: 8,
  color: '#842029',
  padding: 12,
  marginBottom: 15,
  fontSize: 14
};

const visuallyHiddenStyle = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1
};

const moveIconStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  transition: 'opacity 0.35s ease'
};

const moveIconGlyphStyle = {
  position: 'relative',
  width: 48,
  height: 48,
  borderRadius: 24,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 34,
  fontWeight: 'bold',
  lineHeight: 1,
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)'
};

const moveIconHorizontalStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const moveIconVerticalStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const textareaStyle = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #ccc',
  borderRadius: 8,
  padding: 10,
  fontFamily: 'Arial, sans-serif',
  fontSize: 14,
  resize: 'vertical'
};

const secondaryButtonStyle = {
  backgroundColor: '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  padding: '10px 20px',
  cursor: 'pointer',
  fontSize: 14,
  width: '100%',
  marginTop: 15
};

const smallButtonStyle = {
  backgroundColor: '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  padding: '10px 14px',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 'bold'
};

const pillStyle = {
  display: 'inline-block',
  backgroundColor: '#eef7ff',
  color: '#204766',
  border: '1px solid #b6dcff',
  borderRadius: 999,
  padding: '6px 12px',
  fontSize: 13,
  fontWeight: 'bold',
  marginBottom: 12
};

const feedbackPreviewStyle = {
  border: '1px solid #ddd',
  borderRadius: 10,
  padding: 16,
  marginTop: 20,
  backgroundColor: '#f8f9fa'
};

const choicePreviewStyle = {
  border: '1px solid #ccc',
  borderRadius: 8,
  padding: '8px 12px',
  backgroundColor: 'white'
};

const trustedLinkStyle = {
  display: 'block',
  backgroundColor: '#f8f9fa',
  border: '1px solid #ddd',
  borderRadius: 10,
  padding: 14,
  color: '#007bff',
  fontWeight: 'bold',
  textAlign: 'center',
  textDecoration: 'none'
};

const learnMoreButtonStyle = {
  display: 'inline-block',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: 10,
  padding: '14px 24px',
  fontSize: 16,
  fontWeight: 'bold',
  textAlign: 'center',
  textDecoration: 'none'
};

const destinationButtonStyle = {
  ...learnMoreButtonStyle,
  backgroundColor: '#198754'
};

const destinationPreviewStyle = {
  border: '1px solid #d8dee6',
  borderRadius: 10,
  marginTop: 20,
  overflow: 'hidden',
  padding: 14,
  backgroundColor: '#f8fbff'
};

const compactDestinationPreviewStyle = {
  ...destinationPreviewStyle,
  backgroundColor: '#fbfdfc',
  padding: 12
};

const compactDestinationCopyStyle = {
  color: '#52616f',
  fontSize: 14,
  lineHeight: 1.45,
  margin: '0 0 12px 0'
};

const destinationHeaderStyle = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  justifyContent: 'space-between',
  marginBottom: 12,
  color: '#25364a'
};

const destinationProviderPillStyle = {
  backgroundColor: '#e8f5ee',
  border: '1px solid #b7e3ca',
  borderRadius: 999,
  color: '#17633a',
  fontSize: 12,
  fontWeight: 'bold',
  padding: '5px 10px'
};

const destinationEmbedStyle = {
  width: '100%',
  minHeight: 180,
  border: '1px solid #ddd',
  borderRadius: 10,
  marginBottom: 12
};

const destinationFallbackVisualStyle = {
  aspectRatio: '16 / 9',
  backgroundColor: '#132033',
  borderRadius: 10,
  marginBottom: 12,
  overflow: 'hidden',
  position: 'relative'
};

const destinationBrandFallbackStyle = {
  alignItems: 'center',
  color: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  letterSpacing: 0,
  textAlign: 'center'
};

const destinationOverlayStyle = {
  background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.72))',
  bottom: 0,
  color: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  left: 0,
  padding: 14,
  position: 'absolute',
  right: 0
};

export default PostLink;
